import { useEffect, useRef } from 'react';
import { Box, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Layout } from '@roc/feature-app-core';
import { Tabs, GridColumnProps } from '@roc/ui';
import { observer } from 'mobx-react';
import { filterProps, noOpComparator } from '@roc/ui/utils';
import { POST_CLOSING_TABS, PostClosingTabsEnum } from '../constants/tabs';
import { usePostClosingDashboardStore } from '../hooks/usePostClosingDashboardStore';
import { PostClosingGrid } from './postClosingGrid';
import { PostClosingToolbar } from './postClosingToolbar';
import { PostClosingActionModal } from './postClosingActionModal';
import { PostClosingNumberModal } from './postClosingNumberModal';
import { PostClosingDateModal } from './postClosingDateModal';
import { PostClosingSpecialistModal } from './postClosingSpecialistModal';

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '100%',
    paddingLeft: '16px',
    paddingRight: '16px',
    margin: '0 auto',
  },
  paper: {
    boxShadow: '0 0.5px 2px rgba(0, 0, 0, 0.08) !important',
    border: '0.5px solid #e0e0e0',
    padding: '16px',
    borderRadius: '12px !important',
    backgroundColor: '#fff',
  },
}));

export const PostClosingDashboard = observer(() => {
  const classes = useStyles();
  const { postClosingDashboardStore: store } = usePostClosingDashboardStore();
  const gridRef = useRef<any>(null);

  useEffect(() => {
    store.resetAndFetchGridData();
    return () => {
      store.reset();
    };
  }, [store.tab]);

  const handleTabChange = (_: any, index: number) => {
    store.setTab(POST_CLOSING_TABS[index].value);
  };

  const getColumns = (): GridColumnProps[] => {
    switch (store.tab) {
      case PostClosingTabsEnum.SPECIALIST_REASSIGNMENT:
        return [
          {
            field: 'assignedLoanId',
            headerName: 'Loan Id',
            width: 90,
            comparator: noOpComparator,
            ...filterProps(),
          },
          {
            field: 'propertyStreetAddress',
            headerName: 'Property Street Address',
            width: 160,
            comparator: noOpComparator,
            ...filterProps(),
          },
          {
            field: 'postClosingSpecialistName',
            headerName: 'Post Closing Specialist',
            width: 160,
            comparator: noOpComparator,
            ...filterProps(),
          },
        ];

      case PostClosingTabsEnum.COLLATERAL_MANAGEMENT:
        return [
          {
            field: 'assignedLoanId',
            headerName: 'Loan Id',
            width: 90,
            comparator: noOpComparator,
            ...filterProps(),
            cellRenderer: 'loanIdLinkRenderer',
          },
          {
            field: 'closingDate',
            headerName: 'Closing Date',
            width: 140,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'dateCellRenderer',
          },
          {
            field: 'loanType',
            headerName: 'Loan Type',
            width: 120,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'loanSubtype',
            headerName: 'Loan Subtype',
            width: 120,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'loanAmount',
            headerName: 'Loan Amount',
            width: 130,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'currencyCellRenderer',
          },
          {
            field: 'attorneyName',
            headerName: 'Attorney Name',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'isEfileCompleted',
            headerName: 'E-File Completed',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'originalDocsReceived',
            headerName: 'Original Docs Received',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'custodial',
            headerName: 'Custodial Flag',
            width: 100,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'custodianShipDate',
            headerName: 'Custodial Ship Date',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'dateCellRenderer',
          },
          {
            field: 'custodianTrackingNumber',
            headerName: 'Custodial Tracking #',
            width: 180,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'integerCellRenderer',
          },
          {
            field: 'tradeId',
            headerName: 'Trade Id',
            width: 120,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'integerCellRenderer',
          },
          {
            field: 'tradeName',
            headerName: 'Trade Name',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'tradeDate',
            headerName: 'Trade Date',
            width: 140,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'dateCellRenderer',
          },
        ];

      case PostClosingTabsEnum.TRAILING_DOCUMENTS:
      default:
        return [
          {
            field: 'assignedLoanId',
            headerName: 'Loan Id',
            width: 90,
            comparator: noOpComparator,
            ...filterProps(),
            cellRenderer: 'loanIdLinkRenderer',
          },
          {
            field: 'closingDate',
            headerName: 'Closing Date',
            width: 140,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'dateCellRenderer',
          },
          {
            field: 'titleCompanyName',
            headerName: 'Title Company',
            width: 160,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'borrowingEntityName',
            headerName: 'Borrowing Entity',
            width: 160,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'attorneyName',
            headerName: 'Attorney',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'investorEntity',
            headerName: 'Investor Entity',
            width: 160,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'tradeId',
            headerName: 'Trade Id',
            width: 120,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'propertyStreetAddress',
            headerName: 'Street Address',
            width: 160,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'propertyCounty',
            headerName: 'County',
            width: 120,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'propertyState',
            headerName: 'State',
            width: 100,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'postClosingSpecialistName',
            headerName: 'Specialist',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'lastUpdate',
            headerName: 'Last Update',
            width: 130,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'dateCellRenderer',
          },
          {
            field: 'lastInteractionFrom',
            headerName: 'Interaction From',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'lastUpdateInternal',
            headerName: 'Last Update Internal',
            width: 160,
            ...filterProps(),
            comparator: noOpComparator,
            cellRenderer: 'dateCellRenderer',
          },
          {
            field: 'lastInteractionFromInternal',
            headerName: 'Last Interaction From Internal',
            width: 200,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'recordedMortgageStatus',
            headerName: 'Recorded Mortgage',
            width: 160,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'recordedALRStatus',
            headerName: 'Recorded ALR',
            width: 140,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'recordedUCC1CountyStatus',
            headerName: 'Recorded UCC1 (County)',
            width: 180,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'recordedUCC1StateStatus',
            headerName: 'Recorded UCC1 (State)',
            width: 180,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'finalTitlePolicyStatus',
            headerName: 'Final Title Policy',
            width: 180,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'recordedCemaStatus',
            headerName: 'Recorded CEMA',
            width: 150,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'recordedBuildingMortgageStatus',
            headerName: 'Recorded Building Mortgage',
            width: 200,
            ...filterProps(),
            comparator: noOpComparator,
          },
          {
            field: 'loanSubtype',
            headerName: 'Loan Subtype',
            width: 140,
            ...filterProps(),
            comparator: noOpComparator,
          },
        ];
    }
  };

  return (
    <Layout title="Post Closing" containerClass={classes.container}>
      <Box p={2}>
        <Box pb={2}>
          <Tabs
            selectedTab={POST_CLOSING_TABS.findIndex(
              t => t.value === store.tab
            )}
            tabs={POST_CLOSING_TABS}
            onTabChange={handleTabChange}
          />
        </Box>

        <Box display="flex" flexDirection="column" height="100%">
          <Box flex={1}>
            <Paper className={classes.paper}>
              <PostClosingGrid
                ref={gridRef}
                store={store}
                columns={getColumns()}
                toolbar={<PostClosingToolbar store={store} />}
                selectModeEnabled={
                  store.tab !== PostClosingTabsEnum.TRAILING_DOCUMENTS
                }
                actions={() => []}
              />
            </Paper>
          </Box>
        </Box>
      </Box>

      {store.modalStore.modalProps?.type === 'boolean' && (
        <PostClosingActionModal
          {...store.modalStore.modalProps}
          onClose={() => {
            store.modalStore.close();
            gridRef.current?.deselectAll?.();
            store.clearSelectedLoanIds();
          }}
          onConfirm={async value => {
            await store.updateCollateralFieldForLoans(
              store.modalStore.modalProps.fieldKey,
              store.modalStore.modalProps.loans,
              value,
              gridRef
            );
            await store.resetAndFetchGridData();
          }}
        />
      )}

      {store.modalStore.modalProps?.type === 'number' && (
        <PostClosingNumberModal
          {...store.modalStore.modalProps}
          onClose={() => {
            store.modalStore.close();
            gridRef.current?.deselectAll?.();
            store.clearSelectedLoanIds();
          }}
          onConfirm={async value => {
            const parsedValue = Math.floor(value);
            await store.updateCollateralFieldForLoans(
              store.modalStore.modalProps.fieldKey,
              store.modalStore.modalProps.loans,
              parsedValue,
              gridRef
            );
            await store.resetAndFetchGridData();
          }}
        />
      )}
      {store.modalStore.modalProps?.type === 'date' && (
        <PostClosingDateModal
          {...store.modalStore.modalProps}
          onClose={() => {
            store.modalStore.close();
            gridRef.current?.deselectAll?.();
            store.clearSelectedLoanIds();
          }}
          onConfirm={async value => {
            await store.updateCollateralFieldForLoans(
              store.modalStore.modalProps.fieldKey,
              store.modalStore.modalProps.loans,
              value,
              gridRef
            );
            await store.resetAndFetchGridData();
          }}
        />
      )}

      {store.modalStore.modalProps?.type === 'specialist' && (
        <PostClosingSpecialistModal
          loans={store.modalStore.modalProps.loans}
          specialists={store.modalStore.modalProps.specialists.data}
          onClose={() => {
            store.modalStore.close();
            gridRef.current?.deselectAll?.();
            store.clearSelectedLoanIds();
          }}
          onConfirm={async email => {
            await store.service.updatePostClosingSpecialistToLoans(
              store.modalStore.modalProps.loans,
              email
            );
            store.modalStore.close();
            gridRef.current?.deselectAll?.();
            store.clearSelectedLoanIds();
            await store.resetAndFetchGridData();
          }}
        />
      )}
    </Layout>
  );
});
